﻿import React, { useState, useEffect } from 'react';
import { CrudFunctions } from '../utilities/crud-functions';
import { CheckWeight } from '../models/check-weight';
import { BatchCheckWeightData } from '../models/batch-check-weight-data';
import ImageHandler from './ImageHandler';
import DataTemplates from '../templates/data-templates';
import { Asset } from '../models/asset';
import { FieldUpdate } from '../models/field-update';
import GlobalDialog from './Modal/GlobalDialog';
import { ModalMetadata } from '../models/modal-metadata';
import { BenchSheetTechnologyType, ImageType, ModalMessageType } from '../models/enums';

import DataGrid, {
    Column,
    Paging,
    Selection,
    Editing
} from 'devextreme-react/data-grid';

import {
    TextBox,
    SelectBox
} from 'devextreme-react';

import { Button } from 'devextreme-react/button';

const decimalFormat = {
    type: "fixedPoint",
    precision: 4
}

export interface Props {
    batchId: string;
    isReadOnly: boolean;
    balances?: Asset[];
    checkWeightData?: BatchCheckWeightData;
    onCheckWeightBalanceChanged?: (fieldUpdate: FieldUpdate) => void;
}

export default function CheckWeightsComponent(props: Props) {
    const [checkWeights, setCheckWeights] =
        useState<CheckWeight[]>();

    const [checkWeightBalanceId, setCheckWeightBalanceId] =
        useState<string>();

    const [checkWeightsString, setCheckWeightsString] =
        useState<string>();

    const [globalDialogMetadata, setGlobalDialogMetadata] =
        useState<ModalMetadata>();

    const [globalDialogVisible, setGlobalDialogVisible] =
        useState<boolean>();

    const [dataLoaded, setDataLoaded] =
        useState<boolean>();

    useEffect(() => {
        getAllData();
    }, [props.batchId]);

    return (
        <div>
            <div className="lq-header">
                <div className="lq-header-title">Check Weights</div>
                <div className="title-bar-spacer" />
            </div>

            <div className="pw-comp-row">
                <div className="pw-form-unit pw-comp-20 pw-margin-bottom-05">
                    <label className="pw-form-unit-label">Balance</label>
                    <SelectBox id="checkWeightBalanceId" className="asset-selectbox"
                        placeholder='NA' readOnly={props.isReadOnly}
                        displayExpr={'assetNumber'} valueExpr={'assetId'}
                        itemRender={DataTemplates.InstrumentItemRender}
                        dataSource={props.balances ? props.balances : []}
                        value={checkWeightBalanceId ? checkWeightBalanceId : undefined}
                        onValueChanged={onCheckWeightBalanceChanged} />
                </div>

                <div className="pw-form-unit pw-comp-30">
                    <div className="pw-comp-row">
                        <div className="title-bar-spacer">
                            <label className="pw-form-unit-label">Add Check Weights</label>
                            <TextBox id="addedCheckWeights" className="textbox" readOnly={props.isReadOnly}
                                onValueChanged={onAddCheckWeightsValueChanged}
                                placeholder=''
                                value={checkWeightsString ? checkWeightsString : ''} >
                            </TextBox>
                        </div>

                        <div className="pw-margin-top-09">
                            <Button className="title-bar-button" disabled={props.isReadOnly}
                                text="" icon="plus" type="normal"
                                hint="add check weights"
                                onClick={onAddCheckWeightValues} />
                        </div>
                    </div>
                </div>
            </div>

            <div className="pw-comp-row">
                <div className="pw-form-unit pw-comp-20">
                    <DataGrid
                        height="10rem"
                        dataSource={checkWeights ? checkWeights : []}
                        onRowRemoving={onCheckWeightRemoving}
                        rowAlternationEnabled={true}
                        showBorders={true}>

                        <Paging enabled={false} />
                        <Selection mode={'single'} />

                        <Editing
                            mode="cell"
                            useIcons={true}
                            allowAdding={!props.isReadOnly}
                            allowUpdating={!props.isReadOnly}
                            allowDeleting={!props.isReadOnly} >
                        </Editing>

                        <Column dataField={'weight'} dataType="number"
                            cellRender={checkWeightCellRender}
                            format={decimalFormat} />

                    </DataGrid>

                </div>


                <div className="pw-form-unit pw-comp-30">
                    <div className="pw-comp-row">
                        <div className="pw-form-unit pw-comp-15">
                            <div className="pw-form-unit-label">Low Check Weight (mg)</div>
                            <div className="pw-readonly-label">{props.checkWeightData ?
                                props.checkWeightData.lowCheckWeight : 'NA'}</div>
                        </div>
                    </div>
                    <div className="pw-comp-row">
                        <div className="pw-form-unit pw-comp-15">
                            <div className="pw-form-unit-label">Target Check Weight (mg)</div>
                            <div className="pw-readonly-label">{props.checkWeightData ?
                                props.checkWeightData.targetCheckWeight : 'NA'}</div>
                        </div>
                    </div>
                    <div className="pw-comp-row">
                        <div className="pw-form-unit pw-comp-15">
                            <div className="pw-form-unit-label">High Check Weight (mg)</div>
                            <div className="pw-readonly-label">{props.checkWeightData ?
                                props.checkWeightData.highCheckWeight : 'NA'}</div>
                        </div>
                    </div>
                </div>
            </div>

            <div className="pw-margin-top-1">
                <ImageHandler parentId={props.batchId}
                    readOnly={props.isReadOnly} canDelete={false}
                    technology={null} imageType={ImageType.WeightTicket}
                    title="Weight Ticket Images" />
            </div>

            {globalDialogVisible &&
                <GlobalDialog metadata={globalDialogMetadata} onClose={clearGlobalDialog} />
            }

        </div>
    );

    function onCheckWeightBalanceChanged(e: any) {
        if (!dataLoaded)
            return;

        setCheckWeightBalanceId(e.value);

        let fieldUpdate: FieldUpdate = {
            field: "checkWeightBalanceId",
            value: e.value,
            rowId: props.batchId
        };

        if (props.onCheckWeightBalanceChanged)
            props.onCheckWeightBalanceChanged(fieldUpdate);
    }

    function onAddCheckWeightsValueChanged(e: any) {
        setCheckWeightsString(e.value);
    }

    async function onAddCheckWeightValues() {

        console.log("onAddCheckWeightValues");
        console.log(checkWeightsString);

        if (!checkWeightsString)
            return;

        let weights = checkWeightsString.split(/[,\s]/).filter((w: any) => w);
        let checkWeights: CheckWeight[] = [];

        for (let w of weights) {
            let f = parseFloat(w);

            if (f)
                checkWeights.push({ batchId: props.batchId, weight: f });
        }

        console.log("checkWeights");
        console.log(checkWeights);

        if (checkWeights.length > 0) {
            await insertCheckWeights(checkWeights)
                .then((data) => {

                    //TODO:

                    /*
                    if (checkWeights) {
                        let updatedCheckWeights = [...data, ...batch.checkWeights];

                        setCheckWeightsString(undefined);

                        setBatch({ ...batch, checkWeights: updatedCheckWeights });
                    }
                    */

                    //i think this:
                    setCheckWeights(data);
                    setCheckWeightsString(undefined);


                })
                .catch((res) => {
                    showMessage(res, ModalMessageType.Error, "Error Inserting Check Weights");
                });
        }
    }

    //GET functions

    async function onCheckWeightRemoving(e: any) {
        if (e.data && e.data.checkWeightId) {

            await removeCheckWeight(e.data.checkWeightId)
                .catch((res) => {
                    showMessage(res, ModalMessageType.Error, "Error Deleting Check Weights");
                });
        }
    }

    async function getCheckWeights() {
        await CrudFunctions.getData(`products/checkweightsbybatchid/${props.batchId}`)
            .then(data => {
                console.log(data);

                setCheckWeights(data);
            })
            .catch((res) => {
                showMessage(res, ModalMessageType.Error, "Error Retrieving Check Weights");
            });
    }

    async function getCheckWeightBalance() {
        await CrudFunctions.getData(`products/checkweightbalanceidbybatchid/${props.batchId}`)
            .then(data => {
                console.log(data);

                setCheckWeightBalanceId(data.value);
            })
            .catch((res) => {
                showMessage(res, ModalMessageType.Error, "Error Retrieving Balance");
            });
    }

    // POST functions

    async function insertCheckWeights(checkWeights: CheckWeight[]) {
        return await CrudFunctions.postData("products/checkweights", checkWeights);
    }

    //DELETE functions

    async function removeCheckWeight(checkWeightId: string) {
        return await CrudFunctions.deleteData(`products/checkweight/${checkWeightId}`)
    }

    async function getAllData() {
        await Promise.all([
            getCheckWeights(),
            getCheckWeightBalance(),
        ])
            .then(() => setDataLoaded(true));
    }

    //render

    function checkWeightCellRender(e: any) {
        if (e.data && e.data.weight) {
            if (props.checkWeightData) {
                if (props.checkWeightData && props.checkWeightData.highCheckWeight &&
                    props.checkWeightData.lowCheckWeight) {
                    const weight = e.data.weight;

                    if (weight > props.checkWeightData.highCheckWeight ||
                        weight < props.checkWeightData.lowCheckWeight) {
                        return (<div className="invalid-data ">{e.data.weight}</div>);
                    }
                    else
                        return (<div className="valid-data ">{e.data.weight}</div>);
                }
            }
            else {
                return <div>{e.data.weight}</div>;
            }
        }
        else
            return <div></div>;
    }

    function showMessage(res: any, mode: ModalMessageType, title: string) {
        let mm: ModalMetadata = {
            error: res,
            mode: mode,
            title: title
        };

        setGlobalDialogMetadata(mm);
        setGlobalDialogVisible(true);
    }

    function clearGlobalDialog() {
        setGlobalDialogMetadata(null);
        setGlobalDialogVisible(false);
    }


}